// Side-effect imports: <std-dsh-turn-row> must be registered before this
// hydrates one, and a running step's mark is a <std-state-dot>. Both classes
// are otherwise referenced only as types or through their factories.
import "./row";
import "../turn/state-dot";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Typography } from "@tento-ui/ui/typography";
import { StateDot } from "../turn/state-dot";
import type { DshTurnRow } from "./row";

/** `plan`, in the dsh register — a to-do list folded onto one row.
 *
 * The native skin spends a stepper on this: a marker column, a rail, one row
 * per step, always open. dsh spends ONE row, because of what a reader
 * actually asks a plan in a live transcript — not "what are the four steps"
 * but "where is it, and how far in". The collapsed face answers both in a
 * line: the current step as the subject, a count as the suffix.
 *
 * Which step is "current" is a rule, not a field, and it is the only
 * interesting decision in the file: the first RUNNING step, else the first
 * PENDING one, else the last. The three arms are the three states a plan can
 * be in — under way, about to start, finished — and the last arm is what
 * keeps a completed plan showing its final step rather than going blank.
 *
 * The count is done over TOTAL, not done over remaining, because it is read
 * against the subject beside it: a step name and "3/7" is a position, and a
 * position is what a progress line is for.
 *
 * The subject drops when the row opens — the row's default — because the
 * checklist underneath contains the current step verbatim. The count does
 * not: it is the one fact the body never states.
 *
 * Expanded, the whole checklist. A step's status shows in its MARK before its
 * colour — a check for done, the shared state dot for running, a dim middot
 * for pending — so the list survives a theme that has flattened the greys.
 *
 * The checklist is built as elements with INLINE styles: it is handed to the
 * row, which hands it to the accordion, so it lands two shadow roots away
 * from this file's stylesheet. Typography's sheet is in every shadow root, so
 * the ramp classes still resolve and only the values travel. */
@component({ name: "std-dsh-turn-plan" })
export class DshTurnPlan extends LonaWebComponent {
  private $row: DshTurnRow = this.hydrate("row");

  static makeWith(props: DshTurnPlan.Props): DshTurnPlan {
    return (DshTurnPlan.make() as DshTurnPlan).bind(props);
  }

  /** Idempotent: the subject, the count and the whole checklist are derived
   * from `props.steps` on every call, and the row replaces its tree. */
  bind(props: DshTurnPlan.Props): this {
    const steps = props.steps;
    const done = steps.filter((step) => step.status === "done").length;
    const running = steps.some((step) => step.status === "running");
    this.$row.bind({
      kind: "plan",
      label: DshTurnPlan.LABEL,
      subject: DshTurnPlan.current(steps),
      suffix: steps.length === 0 ? null : `${done}/${steps.length}`,
      // The glare, not a mark: a plan with a step in flight is a row that is
      // still moving, and the row already owns how that reads.
      state: running ? "running" : "idle",
      body: steps.length === 0 ? null : DshTurnPlan.buildBody(steps),
    });
    return this;
  }

  /** The checklist. Its margin and indent are NOT set here — the row writes
   * them onto this element, because an element handed to the accordion as
   * content becomes the padded box itself. */
  private static buildBody(steps: DshTurnPlan.Step[]): HTMLElement {
    const $body = document.createElement("div");
    Object.assign($body.style, {
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    });
    for (const step of steps) $body.appendChild(DshTurnPlan.buildStep(step));
    return $body;
  }

  private static buildStep(step: DshTurnPlan.Step): HTMLElement {
    const $line = document.createElement("div");
    Object.assign($line.style, {
      display: "flex",
      alignItems: "center",
      minHeight: "20px",
      minWidth: "0",
    });
    $line.append(
      DshTurnPlan.buildMark(step.status),
      DshTurnPlan.buildStepText(step),
    );
    return $line;
  }

  /** A 14px box whatever is in it, so the step text shares one left edge down
   * the list. A running step gets the shared state dot — the same mark the
   * rest of the transcript spends on "in progress", rather than a spinner
   * this file would have to invent and then keep in step with. */
  private static buildMark(status: DshTurnPlan.StepStatus): HTMLElement {
    const $box = document.createElement("span");
    Object.assign($box.style, {
      flex: "none",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "14px",
      height: "14px",
      marginRight: "8px",
    });
    if (status === "running") {
      // 14px and not the row's 10: this IS the dot's ongoing matrix — 3 cells
      // of 4px separated by 1px gutters — and it is the one size at which the
      // viewBox maps 1:1 to device pixels. The row lead box shrinks the dot
      // because it draws the circle face, which has no grid to miss.
      const $dot = StateDot.makeWith({ state: "ongoing" });
      $dot.style.setProperty("--state-dot-size", "14px");
      $box.appendChild($dot);
      return $box;
    }
    const $glyph = Typography.small(status === "done" ? "✓" : "·");
    Object.assign($glyph.style, {
      marginTop: "0",
      marginBottom: "0",
      color: status === "done"
        ? "var(--fg-green, #2da44e)"
        : "var(--text-ter, #8b93a7)",
      opacity: status === "done" ? "1" : "0.7",
    });
    $box.appendChild($glyph);
    return $box;
  }

  /** Pending is DIM; done and running are not. The three states differ in
   * mark first and in ink second, because a reader scanning a checklist is
   * looking for the boundary between what has happened and what has not, and
   * one signal carrying that boundary is one signal a theme can flatten. */
  private static buildStepText(step: DshTurnPlan.Step): HTMLElement {
    const $text = Typography.small(step.step);
    Object.assign($text.style, {
      flex: "1",
      minWidth: "0",
      marginTop: "0",
      marginBottom: "0",
      color: step.status === "pending"
        ? "var(--text-ter, #8b93a7)"
        : "var(--text-sec, #6a7186)",
      opacity: step.status === "pending" ? "0.75" : "1",
      overflowWrap: "anywhere",
    });
    return $text;
  }

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-dsh-turn-row id="row"></std-dsh-turn-row>
  `;
}

export namespace DshTurnPlan {
  export type StepStatus = "pending" | "running" | "done";

  export type Step = {
    step: string;
    status: StepStatus;
  };

  /** The row's leading glyph comes from `DshTurnRow.GLYPH.plan`. Only the
   * WORD is this file's — "To-do" rather than "Plan", because the row is a
   * list of things not yet done and the reader is counting them. */
  export const LABEL = "To-do";

  /** The step the row is ABOUT. See the note above the class: the first
   * running step, else the first pending one, else the last — under way,
   * about to start, finished. Empty for an empty plan, which is a plan a
   * producer should not have sent but a row still has to survive. */
  export function current(steps: readonly Step[]): string {
    const running = steps.find((step) => step.status === "running");
    if (running != null) return running.step;
    const pending = steps.find((step) => step.status === "pending");
    if (pending != null) return pending.step;
    return steps[steps.length - 1]?.step ?? "";
  }

  export type Props = {
    steps: Step[];
  };
}
